import React, { useState, useRef, useEffect } from "react";
import Swal from "sweetalert2";
import axios from "axios";
import { useLanguage } from "../language/LanguageContext";

function AppUpload({ emailUser }) {
  const { t } = useLanguage();
  const [excelFile, setExcelFile] = useState(null);
  const [uploaderEmail, setUploaderEmail] = useState("");
  const [manualApp, setManualApp] = useState({ appId: "", appName: "", uploaderEmail: "" });
  const [appDetails, setAppDetails] = useState([]);
  const [allApps, setAllApps] = useState([]);
  const [myAppCounts, setMyAppCounts] = useState({});
  const [allAppCounts, setAllAppCounts] = useState({});
  const [isFetchingApps, setIsFetchingApps] = useState(false);
  const [activeTab, setActiveTab] = useState("myApps");
  const [uploadTab, setUploadTab] = useState("excel");
  const [isEditing, setIsEditing] = useState(false);
  const [editApp, setEditApp] = useState({ appId: "", appIdField: "", appName: "", uploaderEmail: "" });

  // New states for filters
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedAppId, setSelectedAppId] = useState("");

  const excelFileInputRef = useRef();
  const ServerUrl = "https://voice-sample.tpvai.com/b/";
  const templateUrl = "https://voice-sample.tpvai.com/b/download/channel-template/AppID.xlsx";

  useEffect(() => {
    if (activeTab === "myApps") {
      fetchAppDetails();
    } else {
      fetchAllApps();
    }
  }, [activeTab]);

  const handleExcelFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const fileExtension = file.name.split(".").pop().toLowerCase();
      if (fileExtension !== "xlsx" && fileExtension !== "xls") {
        Swal.fire("Error", t("Only .xlsx or .xls files are allowed."), "error");
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        Swal.fire("Error", t("File size should be 5MB or less."), "error");
        return;
      }
      setExcelFile(file);
    }
  };

  const handleUploaderEmailChange = (e) => {
    setUploaderEmail(e.target.value);
  };

  const handleManualInputChange = (e) => {
    const { name, value } = e.target;
    setManualApp((prev) => ({ ...prev, [name]: value }));
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateAppId = (appId) => {
    const appIdRegex = /^[a-zA-Z0-9_.,-]+$/;
    console.log(`Validating appId: ${appId}, Result: ${appIdRegex.test(appId)}`);
    return appIdRegex.test(appId);
  };

  const handleExcelUpload = async () => {
    if (!excelFile) {
      Swal.fire("Error", t("Please select an Excel file to upload."), "error");
      return;
    }
    if (!uploaderEmail || !validateEmail(uploaderEmail)) {
      Swal.fire("Error", t("Please provide a valid uploader email."), "error");
      return;
    }

    const formData = new FormData();
    formData.append("excelFile", excelFile);
    formData.append("email", emailUser);
    formData.append("uploaderEmail", uploaderEmail);
    try {
      const response = await axios.post(`${ServerUrl}upload_apps`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      Swal.fire({
        icon: "success",
        title: t("Success"),
        html: `
          <p>${t("Apps processed successfully!")}</p>
          <p>${t("Inserted Apps")}: ${response.data.count}</p>
          <p>${t("Skipped Duplicates")}: ${response.data.skippedCount || 0}</p>
          ${response.data.skippedApps?.length > 0 ? `
            <p>${t("Skipped Apps")}: ${response.data.skippedApps
              .map((app) => `${app.appId}: ${app.appName}`)
              .join(", ")}</p>
          ` : ""}
          ${response.data.invalidRows?.length > 0 ? `
            <p>${t("Invalid Rows")}: ${response.data.invalidRows
              .map((row) => `${row.appId}: ${row.appName} (${row.error})`)
              .join(", ")}</p>
          ` : ""}
          <p>${t("App Counts")}: ${Object.entries(response.data.appCounts || {})
            .map(([appId, count]) => `${appId}: ${count}`)
            .join(", ")}</p>
        `,
      });
      setExcelFile(null);
      setUploaderEmail("");
      if (excelFileInputRef.current) excelFileInputRef.current.value = "";
      fetchAppDetails();
      setActiveTab("myApps");
    } catch (error) {
      Swal.fire("Error", error.response?.data?.message || t("Failed to upload apps"), "error");
    }
  };

  const handleManualUpload = async () => {
    const { appId, appName, uploaderEmail } = manualApp;
    if (!appId || !appName || !uploaderEmail) {
      Swal.fire("Error", t("App ID, app name, and uploader email are required."), "error");
      return;
    }
    if (!validateEmail(uploaderEmail)) {
      Swal.fire("Error", t("Please provide a valid uploader email."), "error");
      return;
    }
    if (!validateAppId(appId)) {
      Swal.fire("Error", t("Invalid app ID format (alphanumeric and underscores only)."), "error");
      return;
    }

    try {
      const response = await axios.post(`${ServerUrl}upload_single_app`, {
        email: emailUser,
        appId: appId.trim(),
        appName: appName.trim(),
        uploaderEmail,
      });
      Swal.fire({
        icon: "success",
        title: t("Success"),
        html: `
          <p>${t("App processed successfully!")}</p>
          <p>${t("Inserted Apps")}: ${response.data.count}</p>
          ${response.data.skipped ? `
            <p>${t("Skipped App")}: ${response.data.skippedApp.appId}: ${response.data.skippedApp.appName}</p>
          ` : ""}
          ${response.data.invalid ? `
            <p>${t("Invalid App")}: ${response.data.invalid.appId}: ${response.data.invalid.appName} (${response.data.invalid.error})</p>
          ` : ""}
          <p>${t("App ID")}: ${response.data.appCounts[appId] || 0}</p>
        `,
      });
      setManualApp({ appId: "", appName: "", uploaderEmail: "" });
      fetchAppDetails();
      setActiveTab("myApps");
    } catch (error) {
      Swal.fire("Error", error.response?.data?.message || t("Failed to upload app"), "error");
    }
  };

  const fetchAppDetails = async () => {
    setIsFetchingApps(true);
    try {
      const response = await axios.get(`${ServerUrl}apps?email=${emailUser}`);
      if (response.status === 200) {
        setAppDetails(response.data.data.sort((a, b) => new Date(b.uploadDate) - new Date(a.uploadDate)));
        setMyAppCounts(response.data.appCounts);
      } else {
        throw new Error(t("Failed to fetch app details"));
      }
    } catch (error) {
      // Handle 404 silently for "No apps found" - just set empty state
      if (error.response?.status === 404) {
        setAppDetails([]);
        setMyAppCounts({});
      } else {
        Swal.fire("Error", error.response?.data?.message || t("Failed to fetch apps"), "error");
        setMyAppCounts({});
      }
    } finally {
      setIsFetchingApps(false);
    }
  };

  const fetchAllApps = async () => {
    setIsFetchingApps(true);
    try {
      const response = await axios.get(`${ServerUrl}all_apps`);
      if (response.status === 200) {
        setAllApps(response.data.data.sort((a, b) => new Date(b.uploadDate) - new Date(a.uploadDate)));
        setAllAppCounts(response.data.appCounts);
      } else {
        throw new Error(t("Failed to fetch all apps"));
      }
    } catch (error) {
      // Handle 404 silently for "No apps found" - just set empty state
      if (error.response?.status === 404) {
        setAllApps([]);
        setAllAppCounts({});
      } else {
        Swal.fire("Error", error.response?.data?.message || t("Failed to fetch all apps"), "error");
        setAllAppCounts({});
      }
    } finally {
      setIsFetchingApps(false);
    }
  };

  const handleDeleteApp = async (appId) => {
    const result = await Swal.fire({
      title: t("Are you sure?"),
      text: t("This app will be deleted permanently."),
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: t("Delete"),
      cancelButtonText: t("Cancel"),
    });

    if (result.isConfirmed) {
      try {
        const response = await axios.delete(`${ServerUrl}delete_app`, {
          data: { appId, email: emailUser },
        });
        if (response.status === 200) {
          Swal.fire("Success", t("App deleted successfully!"), "success");
          fetchAppDetails();
        }
      } catch (error) {
        Swal.fire("Error", error.response?.data?.message || t("Failed to delete app"), "error");
      }
    }
  };

  const handleEditApp = (app) => {
    setEditApp({
      appId: app._id,
      appIdField: app.appId,
      appName: app.appName,
      uploaderEmail: app.uploaderEmail,
    });
    setIsEditing(true);
  };

  const handleUpdateApp = async () => {
    if (!editApp.appIdField || !editApp.appName || !editApp.uploaderEmail) {
      Swal.fire("Error", t("App ID, app name, and uploader email are required."), "error");
      return;
    }
    if (!validateEmail(editApp.uploaderEmail)) {
      Swal.fire("Error", t("Please provide a valid uploader email."), "error");
      return;
    }
    if (!validateAppId(editApp.appIdField)) {
      Swal.fire("Error", t("Invalid app ID format (alphanumeric and underscores only)."), "error");
      return;
    }
    try {
      const response = await axios.put(`${ServerUrl}update_app`, {
        appId: editApp.appId,
        email: emailUser,
        appIdField: editApp.appIdField,
        appName: editApp.appName,
        uploaderEmail: editApp.uploaderEmail,
      });
      if (response.status === 200) {
        Swal.fire("Success", t("App updated successfully!"), "success");
        setIsEditing(false);
        fetchAppDetails();
      }
    } catch (error) {
      Swal.fire("Error", error.response?.data?.message || t("Failed to update app"), "error");
    }
  };

  // New filter function to search across all fields
  const filterApps = (apps) => {
    return apps.filter((app) => {
      const queryLower = searchQuery.toLowerCase();
      const matchesSearch =
        app.appId.toLowerCase().includes(queryLower) ||
        app.appName.toLowerCase().includes(queryLower) ||
        app.uploaderEmail.toLowerCase().includes(queryLower) ||
        new Date(app.uploadDate).toLocaleString().toLowerCase().includes(queryLower);
      const matchesAppId = selectedAppId ? app.appId === selectedAppId : true;
      return matchesSearch && matchesAppId;
    });
  };

  // Get unique App IDs for dropdown
  const getUniqueAppIds = (counts) => {
    return Object.keys(counts).sort();
  };

  const renderAppCounts = (counts) => (
    <div className="mt-4">
      <h4 className="text-md font-semibold text-gray-800 mb-2">{t("Apps by App ID")}</h4>
      {Object.keys(counts).length > 0 ? (
        <table className="w-full text-left text-sm text-gray-700 border border-gray-200 rounded-lg">
          <thead>
            <tr className="bg-gray-100">
              <th className="px-4 py-2">{t("App ID")}</th>
              <th className="px-4 py-2">{t("App Count")}</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(counts).map(([appId, count]) => (
              <tr key={appId} className="border-t">
                <td className="px-4 py-2">{appId}</td>
                <td className="px-4 py-2">{count}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className="text-sm text-gray-500">{t("No app counts available.")}</p>
      )}
    </div>
  );

  return (
    <div className="space-y-6 p-4">
      <h2 className="text-xl font-semibold text-gray-800">{t("Upload App Names")}</h2>

      {/* Upload Tabs */}
      <div className="flex border-b border-gray-200 mb-4">
        <button
          className={`px-4 py-2 text-sm font-medium ${
            uploadTab === "excel" ? "border-b-2 border-blue-500 text-blue-500" : "text-gray-500"
          }`}
          onClick={() => setUploadTab("excel")}
        >
          {t("Excel Upload")}
        </button>
        <button
          className={`px-4 py-2 text-sm font-medium ${
            uploadTab === "manual" ? "border-b-2 border-blue-500 text-blue-500" : "text-gray-500"
          }`}
          onClick={() => setUploadTab("manual")}
        >
          {t("Manual Upload")}
        </button>
      </div>

      {/* Upload Section */}
      {uploadTab === "excel" ? (
        <div className="space-y-4">
          <div>
            <label htmlFor="excel-file-input" className="block text-sm font-medium text-gray-700 mb-1">{t("Upload Excel File")}</label>
            <div className="flex items-center space-x-4 border border-gray-300 rounded-lg p-2">
              <button
                onClick={() => excelFileInputRef.current?.click()}
                className="px-4 py-2 border border-blue-500 text-blue-500 rounded-lg hover:bg-blue-500 hover:text-white text-xs sm:text-sm transition"
              >
                {t("Choose File")}
              </button>
              <span className="text-sm text-gray-600">
                {excelFile ? excelFile.name : t("No file chosen")}
              </span>
              <a
                href={templateUrl}
                download
                className="px-4 py-2 border border-blue-500 text-blue-500 rounded-lg hover:bg-blue-500 hover:text-white text-xs sm:text-sm transition"
              >
                {t("Download Template")}
              </a>
              <input
                ref={excelFileInputRef}
                id="excel-file-input"
                type="file"
                accept=".xlsx,.xls"
                onChange={handleExcelFileChange}
                className="hidden"
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">{t("Excel file should contain 'appId' and 'appName' columns.")}</p>
          </div>
          <div>
            <label htmlFor="uploader-email-input" className="block text-sm font-medium text-gray-700 mb-1">{t("Uploader Email")}</label>
            <input
              id="uploader-email-input"
              type="email"
              value={uploaderEmail}
              onChange={handleUploaderEmailChange}
              className="block w-full border border-gray-300 rounded-lg p-2 text-sm"
              placeholder={t("Enter uploader email")}
            />
          </div>
          <button
            onClick={handleExcelUpload}
            className={`w-full sm:w-auto px-4 py-2 rounded text-white text-xs sm:text-sm ${
              !excelFile ? "bg-gray-400 cursor-not-allowed" : "bg-green-500 hover:bg-green-600"
            }`}
            disabled={!excelFile}
          >
            {t("Upload Apps")}
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          <div>
            <label htmlFor="manual-app-id" className="block text-sm font-medium text-gray-700 mb-1">{t("App ID")}</label>
            <input
              id="manual-app-id"
              name="appId"
              type="text"
              value={manualApp.appId}
              onChange={handleManualInputChange}
              className="block w-full border border-gray-300 rounded-lg p-2 text-sm"
              placeholder={t("Enter app ID (alphanumeric and underscores only)")}
            />
          </div>
          <div>
            <label htmlFor="manual-app-name" className="block text-sm font-medium text-gray-700 mb-1">{t("App Name")}</label>
            <input
              id="manual-app-name"
              name="appName"
              type="text"
              value={manualApp.appName}
              onChange={handleManualInputChange}
              className="block w-full border border-gray-300 rounded-lg p-2 text-sm"
              placeholder={t("Enter app name")}
            />
          </div>
          <div>
            <label htmlFor="manual-uploader-email" className="block text-sm font-medium text-gray-700 mb-1">{t("Uploader Email")}</label>
            <input
              id="manual-uploader-email"
              name="uploaderEmail"
              type="email"
              value={manualApp.uploaderEmail}
              onChange={handleManualInputChange}
              className="block w-full border border-gray-300 rounded-lg p-2 text-sm"
              placeholder={t("Enter uploader email")}
            />
          </div>
          <button
            onClick={handleManualUpload}
            className="w-full sm:w-auto px-4 py-2 bg-green-500 text-white rounded-lg text-xs sm:text-sm hover:bg-green-600"
          >
            {t("Upload App")}
          </button>
        </div>
      )}

      {/* App List Tabs */}
      <div className="mt-6">
        <div className="flex border-b border-gray-200">
          <button
            className={`px-4 py-2 text-sm font-medium ${
              activeTab === "myApps" ? "border-b-2 border-blue-500 text-blue-500" : "text-gray-500"
            }`}
            onClick={() => setActiveTab("myApps")}
          >
            {t("My Apps")}
          </button>
          <button
            className={`px-4 py-2 text-sm font-medium ${
              activeTab === "allApps" ? "border-b-2 border-blue-500 text-blue-500" : "text-gray-500"
            }`}
            onClick={() => setActiveTab("allApps")}
          >
            {t("All Apps")}
          </button>
        </div>

        {/* App List */}
        <h3 className="text-lg font-semibold text-gray-800 my-4">
          {activeTab === "myApps" ? t("My Uploaded Apps") : t("All Uploaded Apps")}
        </h3>

        {/* New Filter UI */}
        <div className="flex space-x-4 mb-4">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t("Search by ID, name, email, or date")}
            className="border border-gray-300 rounded-lg p-2 text-sm flex-1"
          />
          <select
            value={selectedAppId}
            onChange={(e) => setSelectedAppId(e.target.value)}
            className="border border-gray-300 rounded-lg p-2 text-sm"
          >
            <option value="">{t("All App IDs")}</option>
            {getUniqueAppIds(activeTab === "myApps" ? myAppCounts : allAppCounts).map((appId) => (
              <option key={appId} value={appId}>
                {appId}
              </option>
            ))}
          </select>
        </div>

        {isFetchingApps ? (
          <div className="flex justify-center items-center h-64">
            <div className="loader"></div>
          </div>
        ) : (
          <div className="space-y-4">
            {/* <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-lg bg-gray-50 p-4">
              {(() => {
                const apps = activeTab === "myApps" ? appDetails : allApps;
                const filteredApps = filterApps(apps);
                return filteredApps.length > 0 ? (
                  <table className="w-full text-left text-sm text-gray-700">
                    <thead>
                      <tr className="bg-gray-100">
                        <th className="px-4 py-2">{t("App ID")}</th>
                        <th className="px-4 py-2">{t("App Name")}</th>
                        <th className="px-4 py-2">{t("Uploader Email")}</th>
                        <th className="px-4 py-2">{t("Upload Date")}</th>
                        {activeTab === "myApps" && <th className="px-4 py-2">{t("Actions")}</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredApps.map((app) => (
                        <tr key={app._id} className="border-t">
                          <td className="px-4 py-2">{app.appId}</td>
                          <td className="px-4 py-2">{app.appName}</td>
                          <td className="px-4 py-2">{app.uploaderEmail}</td>
                          <td className="px-4 py-2">{new Date(app.uploadDate).toLocaleString()}</td>
                          {activeTab === "myApps" && (
                            <td className="px-4 py-2 flex space-x-2">
                              <button
                                onClick={() => handleEditApp(app)}
                                className="text-blue-500 hover:text-blue-700 text-xs sm:text-sm"
                                title={t("Edit App")}
                              >
                                {t("Edit")}
                              </button>
                              <button
                                onClick={() => handleDeleteApp(app._id)}
                                className="text-red-500 hover:text-red-700 text-xs sm:text-sm"
                                title={t("Delete App")}
                              >
                                {t("Delete")}
                              </button>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p className="text-sm text-gray-500">{t("No apps match the filters.")}</p>
                );
              })()}
            </div> */}
            <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-lg bg-gray-50 p-4">
  {(() => {
    const apps = activeTab === "myApps" ? appDetails : allApps;
    const filteredApps = filterApps(apps);
    return filteredApps.length > 0 ? (
      <table className="w-full text-left text-sm text-gray-700">
        <thead>
          <tr className="bg-gray-100">
            <th className="px-4 py-2">{t("App ID")}</th>
            <th className="px-4 py-2">{t("App Name")}</th>
            <th className="px-4 py-2">{t("Uploader Email")}</th>
            <th className="px-4 py-2">{t("Upload Date")}</th>
            <th className="px-4 py-2">{t("Actions")}</th> {/* Always show Actions */}
          </tr>
        </thead>
        <tbody>
          {filteredApps.map((app) => (
            <tr key={app._id} className="border-t">
              <td className="px-4 py-2">{app.appId}</td>
              <td className="px-4 py-2">{app.appName}</td>
              <td className="px-4 py-2">{app.uploaderEmail}</td>
              <td className="px-4 py-2">{new Date(app.uploadDate).toLocaleString()}</td>
              <td className="px-4 py-2 flex space-x-2"> {/* Always show Actions */}
                <button
                  onClick={() => handleEditApp(app)}
                  className="text-blue-500 hover:text-blue-700 text-xs sm:text-sm"
                  title={t("Edit App")}
                >
                  {t("Edit")}
                </button>
                <button
                  onClick={() => handleDeleteApp(app._id)}
                  className="text-red-500 hover:text-red-700 text-xs sm:text-sm"
                  title={t("Delete App")}
                >
                  {t("Delete")}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    ) : (
      <p className="text-sm text-gray-500">{t("No apps match the filters.")}</p>
    );
  })()}
</div>

            {renderAppCounts(activeTab === "myApps" ? myAppCounts : allAppCounts)}
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {isEditing && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">{t("Edit App")}</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">{t("App ID")}</label>
                <input
                  type="text"
                  value={editApp.appIdField}
                  onChange={(e) => setEditApp({ ...editApp, appIdField: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-lg p-2 text-sm"
                  placeholder={t("Enter app ID (alphanumeric and underscores only)")}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">{t("App Name")}</label>
                <input
                  type="text"
                  value={editApp.appName}
                  onChange={(e) => setEditApp({ ...editApp, appName: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-lg p-2 text-sm"
                  placeholder={t("Enter app name")}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">{t("Uploader Email")}</label>
                <input
                  type="email"
                  value={editApp.uploaderEmail}
                  onChange={(e) => setEditApp({ ...editApp, uploaderEmail: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-lg p-2 text-sm"
                  placeholder={t("Enter uploader email")}
                />
              </div>
            </div>
            <div className="mt-6 flex justify-end space-x-2">
              <button
                onClick={() => setIsEditing(false)}
                className="px-4 py-2 bg-gray-300 text-gray-800 rounded-lg text-sm hover:bg-gray-400"
              >
                {t("Cancel")}
              </button>
              <button
                onClick={handleUpdateApp}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg text-sm hover:bg-blue-600"
              >
                {t("Save")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AppUpload;
